import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/context/Auth";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";
import { Trash2 } from "lucide-react";
import { ConfirmationDialog } from "./ConfirmationDialog";

const fmtDate = (ts?: string | null) => {
  if (!ts) return "—";
  const d = new Date(ts);
  if (isNaN(d.getTime())) return "—";
  return format(d, "MMM d, yyyy p");
};

interface Assessment {
  id: string;
  org_id: string;
  org_name: string;
  questionnaire_id: string;
  questionnaire_title: string;
  status: string;
  created_at: string;
  submitted_at: string | null;
}

interface AdminAssessmentsTableProps {
  onLogAction: (action: string, tableName?: string, recordId?: string, oldValues?: any, newValues?: any, reason?: string) => Promise<void>;
}

export const AdminAssessmentsTable = ({ onLogAction }: AdminAssessmentsTableProps) => {
  const navigate = useNavigate();
  const { session, loading: authLoading } = useAuth();
  const user = session?.user;
  const [assessments, setAssessments] = useState<Assessment[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [showArchived, setShowArchived] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Assessment | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  const { toast } = useToast();

  useEffect(() => {
    if (authLoading || !user) return;
    loadAssessments();
  }, [showArchived, authLoading, user]);

  const loadAssessments = async () => {
    console.log("[Admin] loading assessments via admin_list_assessments_enriched RPC");
    try {
      setLoading(true);
      const { data, error } = await (supabase as any).rpc('admin_list_assessments_enriched', { p_org_id: null });
      if (error) {
        console.error("admin_list_assessments_enriched RPC error:", error);
        toast({ title: "Error", description: "Failed to load assessments via RPC", variant: "destructive" });
        setAssessments([]);
        return;
      }
      console.log("[Admin] Loaded", data?.length, "assessments");
      setAssessments(data || []);
    } catch (error) {
      console.error('Error calling admin_list_assessments_enriched:', error);
      toast({ title: "Error", description: "Failed to load assessments via RPC", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handleRowClick = (assessmentId: string) => {
    navigate(`/assessment/${assessmentId}`, { state: { fromAdminConsole: true } });
  };

  const handleDelete = async (reason: string) => {
    if (!deleteTarget) return;
    setActionLoading(true);
    try {
      const { error } = await (supabase as any).rpc('admin_delete_assessment', {
        p_assessment_id: deleteTarget.id,
        p_reason: reason || null,
      });
      if (error) throw error;
      toast({ title: "Assessment deleted", description: `Assessment for ${deleteTarget.org_name} has been permanently deleted.` });
      setDeleteTarget(null);
      await loadAssessments();
      await onLogAction('delete_assessment', 'assessments', deleteTarget.id, { org_name: deleteTarget.org_name, status: deleteTarget.status }, null, reason);
    } catch (error: any) {
      toast({ title: "Error", description: error.message || "Failed to delete assessment", variant: "destructive" });
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case 'Draft': return 'secondary';
      case 'Submitted': return 'default';
      case 'In Review': return 'outline';
      case 'Finalized': return 'destructive';
      default: return 'secondary';
    }
  };

  const filteredAssessments = assessments.filter(assessment => {
    const matchesFilter = !filter ||
      assessment.org_name?.toLowerCase().includes(filter.toLowerCase()) ||
      assessment.questionnaire_title?.toLowerCase().includes(filter.toLowerCase());
    const matchesStatus = statusFilter === "all" || assessment.status === statusFilter;
    return matchesFilter && matchesStatus;
  });

  if (loading) {
    return <div className="flex justify-center p-8">Loading assessments...</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex gap-4 items-center">
        <Input
          placeholder="Search assessments..."
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="max-w-sm"
        />
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="Draft">Draft</SelectItem>
            <SelectItem value="Submitted">Submitted</SelectItem>
            <SelectItem value="In Review">In Review</SelectItem>
            <SelectItem value="Finalized">Finalized</SelectItem>
          </SelectContent>
        </Select>
        <div className="flex items-center space-x-2">
          <Switch id="show-archived" checked={showArchived} onCheckedChange={setShowArchived} />
          <Label htmlFor="show-archived">Show Archived</Label>
        </div>
      </div>

      {filteredAssessments.length === 0 ? (
        <div className="py-10 text-sm text-slate-500 text-center">
          No active assessments found. Try clearing filters or check archive status.
        </div>
      ) : (
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Organization</TableHead>
                <TableHead>Questionnaire</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Created</TableHead>
                <TableHead>Submitted</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredAssessments.map((assessment) => (
                <TableRow
                  key={assessment.id}
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => handleRowClick(assessment.id)}
                >
                  <TableCell>
                    <div className="font-medium">{assessment.org_name || 'Unnamed'}</div>
                  </TableCell>
                  <TableCell>
                    {assessment.questionnaire_title ? (
                      <Badge variant="outline">{assessment.questionnaire_title}</Badge>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge variant={getStatusBadgeVariant(assessment.status)}>
                      {assessment.status}
                    </Badge>
                  </TableCell>
                  <TableCell>{fmtDate(assessment.created_at)}</TableCell>
                  <TableCell>{fmtDate(assessment.submitted_at)}</TableCell>
                  <TableCell className="text-right">
                    <div onClick={(e) => e.stopPropagation()}>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => setDeleteTarget(assessment)}
                        title="Delete assessment"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <ConfirmationDialog
        open={!!deleteTarget}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        title={`Delete Assessment for "${deleteTarget?.org_name}"?`}
        description={`⚠️ This will permanently delete this ${deleteTarget?.status} assessment and ALL related data including responses, findings, evidence, ratings, and AI reviews. This action cannot be undone.`}
        onConfirm={handleDelete}
        confirmText="Delete Assessment"
        variant="destructive"
        loading={actionLoading}
      />
    </div>
  );
};